/**
 * Timeline rendering utilities
 *
 * Pure functions for converting time ↔ x-coordinates,
 * deterministic colour hashing, and overlap detection.
 */
import type {
  MovementSegment,
  GraphEdgeOverlay,
  AttributionSlice,
} from '../types/vehicleSceneTimeline';
import { CHART_PALETTE, PROVENANCE_COLORS, STATUS_COLORS } from '../config/colors';
import { formatUkDateTime, formatUkTime } from '../lib/calc';

// ── Time ↔ pixel conversion ────────────────────────────────────────────────

export interface TimeScale {
  /** Start of visible window (ms since epoch) */
  startMs: number;
  /** End of visible window (ms since epoch) */
  endMs: number;
  /** Total pixel width of the drawing area */
  widthPx: number;
}

/** Convert ISO string to epoch ms */
export const toMs = (iso: string): number => new Date(iso).getTime();

/** Convert epoch ms to x-coordinate within the viewport */
export const msToX = (ms: number, scale: TimeScale): number => {
  const ratio = (ms - scale.startMs) / (scale.endMs - scale.startMs);
  return ratio * scale.widthPx;
};

/** Convert x-coordinate back to epoch ms */
export const xToMs = (x: number, scale: TimeScale): number => {
  const ratio = x / scale.widthPx;
  return scale.startMs + ratio * (scale.endMs - scale.startMs);
};

/** Get pixel rect (x, width) for a time window */
export const timeWindowToRect = (
  fromIso: string,
  toIso: string,
  scale: TimeScale,
): { x: number; width: number } => {
  const x = msToX(toMs(fromIso), scale);
  const x2 = msToX(toMs(toIso), scale);
  return { x, width: Math.max(x2 - x, 1) }; // minimum 1px
};

// ── Zoom helpers ────────────────────────────────────────────────────────────

export interface ZoomPanState {
  /** Zoom level (1.0 = no zoom) */
  zoom: number;
  /** Horizontal offset in ms */
  offsetMs: number;
}

export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 50;

/** Compute visible scale given container width, zoom, and offset */
export const computeScale = (
  windowFromMs: number,
  windowToMs: number,
  containerWidth: number,
  zoomPan: ZoomPanState,
): TimeScale => {
  const totalDuration = windowToMs - windowFromMs;
  const visibleDuration = totalDuration / zoomPan.zoom;
  const startMs = windowFromMs + zoomPan.offsetMs;
  const endMs = startMs + visibleDuration;
  return { startMs, endMs, widthPx: containerWidth };
};

/** Clamp offset to keep viewport within data window */
export const clampOffset = (
  offsetMs: number,
  windowFromMs: number,
  windowToMs: number,
  zoom: number,
): number => {
  const totalDuration = windowToMs - windowFromMs;
  const visibleDuration = totalDuration / zoom;
  const maxOffset = totalDuration - visibleDuration;
  return Math.max(0, Math.min(offsetMs, maxOffset));
};

// ── Deterministic colour mapping ────────────────────────────────────────────

/** FNV-1a 32-bit hash */
const fnv1a = (str: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash;
};

/** Deterministic colour for an entity ID — categorical, so it draws from the
 *  shared chart palette (stable order, one source). */
export const entityColor = (entityId: string): string => {
  const idx = fnv1a(entityId) % CHART_PALETTE.length;
  return CHART_PALETTE[idx];
};

// ── Segment type colours ────────────────────────────────────────────────────

export const SEGMENT_COLORS: Record<string, string> = {
  MOVING: STATUS_COLORS.success,
  STOPPED: STATUS_COLORS.destructive,
  UNKNOWN: 'rgb(var(--muted-foreground))',
};

export const AUTHORITY_COLORS: Record<string, string> = {
  DECLARED: STATUS_COLORS.info,
  OBSERVED: STATUS_COLORS.success,
  INFERRED: PROVENANCE_COLORS.inferred,
  SYSTEM: 'rgb(var(--muted-foreground))',
};

// ── Overlap detection ───────────────────────────────────────────────────────

export const rangesOverlap = (
  aFrom: number,
  aTo: number,
  bFrom: number,
  bTo: number,
): boolean => aFrom < bTo && bFrom < aTo;

/** Find all segments overlapping a given time window */
export const findOverlappingSegments = (
  segments: MovementSegment[],
  fromMs: number,
  toMs: number,
): string[] =>
  segments
    .filter(s => rangesOverlap(toMs_(s.start_ts), toMs_(s.end_ts), fromMs, toMs))
    .map(s => s.segment_id);

/** Find all slices overlapping a given time window */
export const findOverlappingSlices = (
  slices: AttributionSlice[],
  fromMs: number,
  toMs: number,
): string[] =>
  slices
    .filter(s => rangesOverlap(toMs_(s.start_ts), toMs_(s.end_ts), fromMs, toMs))
    .map(s => s.slice_id);

/** Find all edge overlays overlapping a given time window */
export const findOverlappingEdges = (
  edges: GraphEdgeOverlay[],
  fromMs: number,
  toMs: number,
): string[] =>
  edges
    .filter(e => rangesOverlap(toMs_(e.from_ts), toMs_(e.to_ts), fromMs, toMs))
    .map(e => e.edge_id);

// alias to avoid name conflict with exported toMs
const toMs_ = toMs;

// ── Time formatting ─────────────────────────────────────────────────────────

export const formatTime = (ms: number): string => {
  return formatUkTime(new Date(ms), {}, '');
};

export const formatDateTime = (iso: string): string => {
  return formatUkDateTime(iso, '');
};

export const formatDuration = (fromIso: string, toIso: string): string => {
  const ms = toMs(toIso) - toMs(fromIso);
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

/** Format confidence as percentage string */
export const formatConfidence = (c: number): string => `${Math.round(c * 100)}%`;

// ── Tick generation for time ruler ──────────────────────────────────────────

export interface TimeTick {
  ms: number;
  x: number;
  label: string;
  isMajor: boolean;
}

/** Generate time ticks for the ruler */
export const generateTicks = (scale: TimeScale): TimeTick[] => {
  const duration = scale.endMs - scale.startMs;
  // Choose tick interval based on visible duration
  let intervalMs: number;
  let majorEvery: number;
  if (duration <= 30 * 60_000) {
    // ≤30min: 1min ticks, major every 5min
    intervalMs = 60_000;
    majorEvery = 5;
  } else if (duration <= 2 * 3600_000) {
    // ≤2h: 5min ticks, major every 30min
    intervalMs = 5 * 60_000;
    majorEvery = 6;
  } else if (duration <= 8 * 3600_000) {
    // ≤8h: 15min ticks, major every hour
    intervalMs = 15 * 60_000;
    majorEvery = 4;
  } else if (duration <= 24 * 3600_000) {
    // ≤24h: 30min ticks, major every 2h
    intervalMs = 30 * 60_000;
    majorEvery = 4;
  } else {
    // >24h: 1h ticks, major every 6h
    intervalMs = 3600_000;
    majorEvery = 6;
  }

  const ticks: TimeTick[] = [];
  // Snap to interval
  const first = Math.ceil(scale.startMs / intervalMs) * intervalMs;
  let idx = 0;
  for (let ms = first; ms <= scale.endMs; ms += intervalMs) {
    ticks.push({
      ms,
      x: msToX(ms, scale),
      label: formatTime(ms),
      isMajor: idx % majorEvery === 0,
    });
    idx++;
  }
  return ticks;
};

// ── Precomputed ms cache ────────────────────────────────────────────────────

export interface PrecomputedItem {
  id: string;
  startMs: number;
  endMs: number;
}

export const precomputeSegments = (
  segments: MovementSegment[],
): PrecomputedItem[] =>
  segments.map(s => ({
    id: s.segment_id,
    startMs: toMs(s.start_ts),
    endMs: toMs(s.end_ts),
  }));

export const precomputeEdges = (
  edges: GraphEdgeOverlay[],
): PrecomputedItem[] =>
  edges.map(e => ({
    id: e.edge_id,
    startMs: toMs(e.from_ts),
    endMs: toMs(e.to_ts),
  }));

export const precomputeSlices = (
  slices: AttributionSlice[],
): PrecomputedItem[] =>
  slices.map(s => ({
    id: s.slice_id,
    startMs: toMs(s.start_ts),
    endMs: toMs(s.end_ts),
  }));
